/* Native Shopify forms remain responsible for validation, submission and consent. */
export const SIGNUP_KEYS = Object.freeze({
  subscribed: 'ps:acquisition:subscribed:v1',
  dismissed: 'ps:acquisition:dismiss-until:v1',
  pending: 'ps:acquisition:submitted:v1',
  engagedForm: 'ps:acquisition:engaged-form:v1'
});
const memory = new Map();
export function safeRead(storage, key) {
  try { return storage?.getItem(key) ?? memory.get(key) ?? null; }
  catch { return memory.get(key) ?? null; }
}
export function safeWrite(storage, key, value) {
  memory.set(key, String(value));
  try { storage?.setItem(key, String(value)); } catch { /* Session memory still suppresses repeats. */ }
}
function storageFor(type) {
  try { return window[type]; } catch { return null; }
}
export function isEligibleRoute(pageType, pathname) {
  if (/\/(?:cart|search|account|checkout|checkouts|customer_authentication)(?:\/|$)/i.test(pathname)) return false;
  if (/\/(?:collections|products)\/(?:samplers|discovery-set|discovery-sets)(?:\/|$)/i.test(pathname)) return false;
  if (['index', 'collection', 'product'].includes(pageType)) return true;
  return ['blog', 'article'].includes(pageType) && /\/blogs\/the-scent-library(?:\/|$)/i.test(pathname);
}
export function scrollProgress(scrollY, scrollHeight, viewportHeight) {
  const distance = scrollHeight - viewportHeight;
  return distance > 0 ? Math.min(1, Math.max(0, scrollY / distance)) : 0;
}
export function confirmedSignupReturn(search, hasSuccess) {
  return new URLSearchParams(search).get('customer_posted') === 'true' && Boolean(hasSuccess);
}
export function readPending(value, now = Date.now()) {
  try {
    const pending = JSON.parse(value);
    if (!pending || !['popup', 'footer', 'inline', 'waitlist'].includes(pending.source)) return null;
    if (typeof pending.formId !== 'string' || !Number.isFinite(pending.at) || now < pending.at || now - pending.at > 30 * 60 * 1000) return null;
    return pending;
  } catch { return null; }
}
export function ownsReturn(hash, formId, pending) {
  let anchor = '';
  try { anchor = decodeURIComponent(hash.replace(/^#/, '')); } catch { return false; }
  // A different explicit customer form takes precedence over a stale popup submission.
  if (anchor && anchor !== formId && /(?:newsletter|signup|contact|waitlist)/i.test(anchor)) return false;
  return anchor === formId || (pending?.source === 'popup' && pending.formId === formId);
}
export function shouldAutoOpen(state) {
  return state.enabled && state.eligible && !state.knownCustomer && !state.subscribed &&
    !state.dismissed && !state.shown && state.visible && !state.blocked &&
    (state.visibleMs >= state.delayMs || state.scroll >= state.minScroll || state.exitIntent === true);
}
export function isDesktopExitIntent(event, finePointer) {
  return finePointer && event.relatedTarget === null && Number.isFinite(event.clientY) && event.clientY <= 0;
}

const BaseElement = globalThis.HTMLElement ?? class {};
export class PSAcquisition extends BaseElement {
  connectedCallback() {
    if (this._ready) return;
    this._dialog = this.querySelector('dialog');
    this._form = this.querySelector('[data-ps-signup-form]');
    if (!this._dialog?.showModal || !this._form) return;
    this._ready = true;
    this._abort = new AbortController();
    const signal = this._abort.signal;
    this._shown ??= false;
    this._visibleMs = 0;
    this._activeSince = document.hidden ? null : performance.now();
    this._local = storageFor('localStorage');
    this._session = storageFor('sessionStorage');
    this._delayMs = Math.max(6000, Number(this.dataset.engagementSeconds || 6) * 1000);
    this._minScroll = Math.max(.2, Math.min(.8, Number(this.dataset.engagementScroll || 20) / 100));
    this._dismissalDays = Math.max(1, Math.min(30, Number(this.dataset.dismissalDays || 7)));
    this._pending = readPending(safeRead(this._session, SIGNUP_KEYS.pending));
    this._returnRequested = !this._returnHandled && ownsReturn(location.hash, this._form.id, this._pending) &&
      (confirmedSignupReturn(location.search, this.querySelector('[data-ps-signup-success]')) || Boolean(this.querySelector('[data-ps-signup-error]')));

    this.querySelector('[data-ps-close-signup]').addEventListener('click', () => this._dialog.close(), { signal });
    this._dialog.addEventListener('close', () => this._afterClose(), { signal });
    this._dialog.addEventListener('pointerdown', (event) => { this._backdropDown = this._outsidePanel(event); }, { signal });
    this._dialog.addEventListener('click', (event) => {
      if (this._backdropDown && this._outsidePanel(event)) this._dialog.close();
      this._backdropDown = false;
    }, { signal });
    document.addEventListener('click', (event) => {
      const opener = event.target.closest?.('[data-ps-open-signup]');
      if (opener && !event.defaultPrevented && event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && this.open(opener)) event.preventDefault();
    }, { signal, capture: true });
    document.addEventListener('focusin', (event) => {
      if (this._rememberOtherSignup(event.target)) this._evaluate();
    }, { signal, capture: true });
    document.addEventListener('visibilitychange', () => {
      if (this._activeSince !== null) this._visibleMs += performance.now() - this._activeSince;
      this._activeSince = document.hidden ? null : performance.now();
      this._evaluate();
    }, { signal });
    const scheduleEvaluation = () => {
      if (!this._scrollFrame) this._scrollFrame = requestAnimationFrame(() => { this._scrollFrame = null; this._evaluate(); });
    };
    window.addEventListener('scroll', scheduleEvaluation, { passive: true, signal });
    window.addEventListener('resize', scheduleEvaluation, { signal });
    document.addEventListener('mouseout', (event) => {
      if (isDesktopExitIntent(event, window.matchMedia('(hover: hover) and (pointer: fine)').matches)) this._evaluate(true);
    }, { signal });
    document.addEventListener('ps:signup-confirmed', () => this._evaluate(), { signal });
    document.addEventListener('shopify:section:select', (event) => {
      if (globalThis.Shopify?.designMode && (event.target === this.closest('.shopify-section') || event.target.contains(this))) this.open();
    }, { signal });
    this._rememberOtherSignup(document.activeElement);
    this._evaluate();
  }
  disconnectedCallback() {
    this._abort?.abort();
    clearTimeout(this._timer);
    cancelAnimationFrame(this._scrollFrame);
    cancelAnimationFrame(this._focusFrame);
    this._scrollFrame = null;
    this._focusFrame = null;
    if (this._dialog?.open) {
      this._dialog.close();
      document.documentElement.classList.remove('ps-acquisition-is-open');
      this._restoreFocus();
    }
    this._ready = false;
  }
  _outsidePanel(event) {
    if (event.target !== this._dialog) return false;
    const rect = this._dialog.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
  }
  _blocked() {
    return [...document.querySelectorAll('dialog[open], [role="dialog"], [aria-modal="true"], cart-drawer[open], search-drawer[open], x-drawer[open], x-modal[open]')].some((element) => {
      if (element === this._dialog || this.contains(element) || element.contains(this)) return false;
      return element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
    });
  }
  _subscribed() {
    return safeRead(this._local, SIGNUP_KEYS.subscribed) === 'true' || safeRead(this._local, 'theme:popup-filled') === 'true';
  }
  _rememberOtherSignup(target) {
    const form = target?.closest?.('form[data-ps-signup-form]');
    if (!form || form === this._form || form.dataset.psSignupSource === 'popup') return false;
    safeWrite(this._session, SIGNUP_KEYS.engagedForm, 'true');
    return true;
  }
  _otherSignupVisible() {
    return [...document.querySelectorAll('form[data-ps-signup-form]')].some((form) => {
      if (form === this._form || form.dataset.psSignupSource === 'popup' || !form.getClientRects().length) return false;
      const style = getComputedStyle(form);
      if (style.visibility === 'hidden' || style.visibility === 'collapse' || style.opacity === '0') return false;
      const rect = form.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.top < window.innerHeight && rect.bottom > 0 &&
        rect.left < window.innerWidth && rect.right > 0;
    });
  }
  _evaluate(exitIntent = false) {
    clearTimeout(this._timer);
    if (!this.isConnected || this._dialog.open || document.hidden) return;
    if (this._returnRequested) {
      if (this.open()) { this._returnRequested = false; this._returnHandled = true; }
      else this._timer = setTimeout(() => this._evaluate(), 1000);
      return;
    }
    const activeMs = this._visibleMs + (this._activeSince === null ? 0 : performance.now() - this._activeSince);
    const active = document.activeElement;
    const blocked = this._blocked() || this._otherSignupVisible() || Boolean(active?.matches('input, textarea, select, [contenteditable="true"]')) ||
      Boolean(document.querySelector('ps-campaign-tags details[open], .ps-more-filters[open]'));
    const state = {
      enabled: this.dataset.autoEnabled === 'true' && !globalThis.Shopify?.designMode,
      eligible: isEligibleRoute(this.dataset.pageType, location.pathname) &&
        safeRead(this._session, SIGNUP_KEYS.engagedForm) !== 'true' &&
        !document.querySelector('[data-ps-signup-error]:not([data-ps-signup-error="popup"])'),
      knownCustomer: this.dataset.knownCustomer === 'true',
      subscribed: this._subscribed(),
      dismissed: Number(safeRead(this._local, SIGNUP_KEYS.dismissed)) > Date.now(),
      shown: this._shown, visible: !document.hidden, blocked,
      visibleMs: activeMs, delayMs: this._delayMs,
      scroll: scrollProgress(window.scrollY, document.documentElement.scrollHeight, window.innerHeight),
      minScroll: this._minScroll, exitIntent
    };
    if (shouldAutoOpen(state)) { this.open(); return; }
    if (!state.enabled || !state.eligible || state.knownCustomer || state.subscribed || state.dismissed || state.shown) return;
    if (activeMs < this._delayMs) this._timer = setTimeout(() => this._evaluate(), Math.max(250, this._delayMs - activeMs));
    else if (blocked) this._timer = setTimeout(() => this._evaluate(), 1000);
  }
  open(opener = null) {
    if (!this.isConnected || document.hidden || this._dialog.open || this._blocked()) return false;
    this._previousFocus = opener || document.activeElement;
    try { this._dialog.showModal(); } catch { return false; }
    clearTimeout(this._timer);
    this._shown = true;
    document.documentElement.classList.add('ps-acquisition-is-open');
    this._focusFrame = requestAnimationFrame(() => {
      this._focusFrame = null;
      if (!this.isConnected || !this._dialog.open) return;
      (this.querySelector('[data-ps-signup-error], [data-ps-signup-success]') || this.querySelector('h2')).focus({ preventScroll: true });
    });
    return true;
  }
  _afterClose() {
    // A queued native close event must not release a dialog that was re-opened meanwhile.
    if (this._dialog.open) return;
    cancelAnimationFrame(this._focusFrame);
    this._focusFrame = null;
    document.documentElement.classList.remove('ps-acquisition-is-open');
    if (!this._subscribed()) safeWrite(this._local, SIGNUP_KEYS.dismissed, Date.now() + this._dismissalDays * 86400000);
    this._restoreFocus();
  }
  _restoreFocus() {
    if (this._previousFocus?.isConnected && this._previousFocus !== document.body) this._previousFocus.focus({ preventScroll: true });
    this._previousFocus = null;
  }
}

if (globalThis.document && globalThis.customElements) {
  // One module-level bridge covers every native customer signup without replacing submission.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form.matches?.('[data-ps-signup-form]') || !form.id) return;
    safeWrite(storageFor('sessionStorage'), SIGNUP_KEYS.pending, JSON.stringify({ source: form.dataset.psSignupSource, formId: form.id, at: Date.now() }));
  }, { capture: true });
  const confirmReturn = () => {
    if (!confirmedSignupReturn(location.search, document.querySelector('[data-ps-signup-success]'))) return;
    safeWrite(storageFor('localStorage'), SIGNUP_KEYS.subscribed, 'true');
    document.dispatchEvent(new CustomEvent('ps:signup-confirmed'));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', confirmReturn, { once: true });
  else confirmReturn();
  if (!customElements.get('ps-acquisition')) customElements.define('ps-acquisition', PSAcquisition);
}
